import React, { useEffect, useState } from "react";
import categoryService from "@/services/categoryService";
import { getApiErrorMessage } from "@/services/apiClient";
import { showInfo } from "@/store/errorStore";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Plus,
  Edit3,
  Trash2,
  Search as SearchIcon,
  Package,
} from "lucide-react";
import { CATEGORY_ICON_GROUPS } from "./categoryIcons";

// Minúsculas e sem acentos, para a busca de ícones
const normalizeText = (text) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");


export default function CategoriesManagement() {
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ id: "", name: "", icon: "" });
  const [iconQuery, setIconQuery] = useState("");
  const normalizedIconQuery = normalizeText(iconQuery.trim());
  const iconGroups = CATEGORY_ICON_GROUPS.map((group) => ({
    ...group,
    icons: normalizedIconQuery
      ? group.icons.filter(([, keywords]) =>
          normalizeText(keywords).includes(normalizedIconQuery)
        )
      : group.icons,
  })).filter((group) => group.icons.length > 0);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [formError, setFormError] = useState("");
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setFetching(true);
    try {
      setCategories(await categoryService.getAll());
    } catch (e) {
      console.error("Error fetching categories:", e);
      setCategories([]);
    } finally {
      setFetching(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ id: "", name: "", icon: "" });
    setIconQuery("");
    setShowDialog(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ id: cat.id ?? "", name: cat.name ?? "", icon: cat.icon ?? "" });
    setIconQuery("");
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.name || form.name.trim().length === 0) {
      setFormError("Nome é obrigatório");
      return;
    }
    setLoading(true);
    try {
      if (editing) {
        await categoryService.update(editing.id, form);
      } else {
        await categoryService.create(form);
      }
      await fetchCategories();
      setShowDialog(false);
      showInfo(
        editing ? "Categoria atualizada com sucesso" : "Categoria criada com sucesso",
        "Categoria"
      );
    } catch (err) {
      // Ex.: chave duplicada na empresa (400). O modal global também é exibido.
      setFormError(getApiErrorMessage(err) || "Erro ao salvar categoria");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (cat) => {
    setCategoryToDelete(cat);
  };

  const handleConfirmDelete = async () => {
    const cat = categoryToDelete;
    setCategoryToDelete(null);
    if (!cat) return;
    try {
      await categoryService.delete(cat.id);
      showInfo("Categoria excluída com sucesso", "Categoria");
      await fetchCategories();
    } catch (e) {
      // Modal global já exibido pelo interceptor
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Modal de Loading */}
      <Dialog open={fetching} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-sm"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-gray-200 border-t-[#1b5e20] rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Package className="h-6 w-6 text-[#1b5e20]" />
              </div>
            </div>
            <p className="mt-6 text-base font-semibold text-gray-900 text-center">
              Carregando categorias...
            </p>
            <p className="mt-2 text-sm text-gray-500 text-center">
              Por favor, aguarde
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4 w-full md:w-auto">
          <h2 className="text-3xl font-bold text-gray-900">
            Gestão de Categorias
          </h2>
        </div>

        <div className="flex gap-3 items-center w-full md:w-auto">
          <div className="relative flex-1 md:flex-none">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Pesquisar por nome ou id..."
              className="pl-10 w-full md:w-64"
            />
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button
                onClick={openCreate}
                className=""
                style={{ background: "#1b5e20" }}
              >
                <Plus className="h-4 w-4 mr-2" /> Nova Categoria
              </Button>
            </DialogTrigger>

            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Editar Categoria" : "Nova Categoria"}
                </DialogTitle>
                <DialogDescription>
                  {editing
                    ? "Atualize a categoria."
                    : "Adicione uma nova categoria."}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="id">Chave (id)</Label>
                  <Input
                    id="id"
                    value={form.id}
                    onChange={(e) => setForm({ ...form, id: e.target.value })}
                    placeholder="ex: bebida_gelada"
                  />
                </div>
                <div>
                  <Label htmlFor="icon">Ícone</Label>
                  <Input
                    value={iconQuery}
                    onChange={(e) => setIconQuery(e.target.value)}
                    placeholder="Buscar ícone (ex: pizza, pet, roupa)"
                    className="mb-2"
                  />
                  <div className="max-h-56 overflow-y-auto border rounded p-2 mb-2 space-y-3">
                    {iconGroups.length === 0 && (
                      <p className="text-sm text-gray-500">
                        Nenhum ícone encontrado.
                      </p>
                    )}
                    {iconGroups.map((group) => (
                      <div key={group.label}>
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          {group.label}
                        </p>
                        <div className="flex gap-1 flex-wrap">
                          {group.icons.map(([ico, keywords]) => (
                            <button
                              type="button"
                              key={ico}
                              className={`border rounded px-2 py-1 text-xl bg-white hover:bg-gray-100 focus:outline-none ${
                                form.icon === ico
                                  ? "border-red-600"
                                  : "border-gray-300"
                              }`}
                              onClick={() => setForm({ ...form, icon: ico })}
                              title={keywords}
                              aria-label={`Selecionar ícone ${keywords}`}
                            >
                              {ico}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Input
                    id="icon"
                    value={form.icon}
                    onChange={(e) => setForm({ ...form, icon: e.target.value })}
                    placeholder="Emoji ou texto curto"
                  />
                </div>

                {formError && (
                  <div className="text-red-600 text-sm">{formError}</div>
                )}

                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowDialog(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {editing ? "Atualizar" : "Criar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(() => {
          const q = query.trim().toLowerCase();
          const list = q
            ? categories.filter((c) =>
                `${String(c.id || "").toLowerCase()} ${String(
                  c.name || ""
                ).toLowerCase()}`.includes(q)
              )
            : categories;

          if (!fetching && !list.length) {
            return (
              <div className="col-span-full text-center text-gray-500 py-10">
                {q
                  ? "Nenhuma categoria encontrada."
                  : "Nenhuma categoria cadastrada."}
              </div>
            );
          }

          return list.map((cat) => (
            <Card
              key={cat.id}
              className="overflow-hidden hover:shadow-lg transition-shadow"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">
                    {cat.icon} {cat.name}
                  </h3>
                  <span className="text-sm text-gray-500">ID: {cat.id}</span>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(cat)}
                  >
                    <Edit3 className="h-4 w-4 mr-1" /> Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(cat)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Excluir
                  </Button>
                </div>
              </CardContent>
            </Card>
          ));
        })()}
      </div>
      <AlertDialog
        open={!!categoryToDelete}
        onOpenChange={(open) => !open && setCategoryToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir categoria</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a categoria "
              {categoryToDelete?.name}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
